import React, { useState, useEffect, useRef } from "react";
import { RoleProps } from "modules/auth/role/store/actionType";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

interface MultiSelectFormProps {
  id: string;
  label: string;
  name: string;
  value: string[];
  option: RoleProps[];
  onChange: (selected: string[]) => void;
  expandParent?: boolean; 
}

const MultiSelectForm: React.FC<MultiSelectFormProps> = ({
  label,
  name,
  value,
  option,
  onChange,
  expandParent = false, 
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleCheckboxChange = (roleId: string) => {
    let updated: string[];
    if (value.includes(roleId)) {
      updated = value.filter((id) => id !== roleId);
    } else {
      updated = [...value, roleId];
    }
    onChange(updated);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`w-full ${expandParent ? "" : "relative"}`} ref={dropdownRef}>
      <label className="block text-sm/6 font-medium text-left">{label}</label>

      <div
        className="border rounded-lg p-2 flex items-center justify-between cursor-pointer bg-white"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm text-gray-800">
          {value.length > 0
            ? `${value.length} role${value.length > 1 ? "s" : ""} selected`
            : "Select roles"}
        </span>

        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
      </div>

      {open && (
        <div
          className={`mt-2 w-full bg-white border rounded-lg shadow-lg max-h-56 overflow-auto z-20
            ${expandParent ? "" : "absolute"}`}
        >
          {option.map((role) => (
            <label
              key={role.id}
              className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                name={name}
                value={role.id}
                checked={value.includes(role.id)}
                onChange={() => handleCheckboxChange(role.id)}
                className="mr-2"
              />
              <span className="text-sm text-gray-800">{role.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectForm;
